/**
 * Set localStorage key with ttl
 * @param {string} key
 * @param value
 * @param {number} ttl - Time to live in milliseconds
 * @param {object} $bugsnag - Bugsnag instance for error reporting
 */
export function setWithExpiry(key, value, ttl, $bugsnag = {}) {
  const now = new Date();

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl
  };
  // Attempt to set the item in localStorage
  try {
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    if (
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    ) {
      // Attempt to remove keys with known prefix
      const offenderPrefix = 'offenders-';
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const keyName = localStorage.key(i);
        if (keyName && keyName.startsWith(offenderPrefix)) {
          keysToRemove.push(keyName);
        }
      }

      // Delete matching keys
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      // Retry storing the item
      try {
        localStorage.setItem(key, JSON.stringify(item));
      } catch (retryError) {
        // Still failing — notify Bugsnag and log
        $bugsnag?.notify(retryError, event => {
          event.addMetadata('vue', {
            function: 'setWithExpiry',
            file: 'local-storage.js',
            note: 'Storage failed after cleanup'
          });
        });
        console.error('Retry failed:', retryError);
      }
    }
  }
}

/**
 * Get localStorage key
 * @param {string} key
 * @returns {null|*}
 */
export function getWithExpiry(key) {
  const itemStr = localStorage.getItem(key);

  // if the item doesn't exist, return null
  if (!itemStr) {
    return null;
  }

  const item = JSON.parse(itemStr);
  const now = new Date();

  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    // If the item is expired, delete the item from storage
    // and return null
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
}
